import { err, ok, type Result } from 'neverthrow';
import { type z } from 'zod';

import { type SdkError } from '../types.ts';

interface SafeParser<T> {
  safeParse: (
    input: unknown
  ) => { success: true; data: T } | { success: false; error: { issues: z.ZodIssue[] } };
}

/**
 * Runs a schema's `safeParse` and lifts the outcome into a `Result`. On
 * failure, the Zod issues are wrapped in a `VALIDATION_ERROR`. Use this as the
 * first stage of any SDK mutation pipeline.
 */
export function parse<T>(schema: SafeParser<T>, input: unknown): Result<T, SdkError> {
  const parsed = schema.safeParse(input);

  if (!parsed.success) {
    return err({ type: 'VALIDATION_ERROR', issues: parsed.error.issues });
  }

  return ok(parsed.data);
}

/**
 * Executes a mutation and lifts the outcome into a `Result`, catching any
 * unexpected throw as `INTERNAL_ERROR`. Use this as the final stage of a
 * pipeline, after validation and business-rule guards have passed — so a
 * thrown error here genuinely is unexpected.
 */
export function tryMutate<T>(fn: () => T): Result<T, SdkError> {
  try {
    return ok(fn());
  } catch (e) {
    return err({
      type: 'INTERNAL_ERROR',
      message: e instanceof Error ? e.message : 'Unknown mutation error'
    });
  }
}
